import { normalizeResponsiveMapping } from "./responsiveMappings.js";
import { validateManagedTargeting } from "./targeting.js";

export const GPT_SCRIPT_URL = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
const slots = new Map();
let servicesEnabled = false;
let loader = null;
const displayed = new Set();

export function resetGptRuntimeForTests() {
  slots.clear();
  displayed.clear();
  servicesEnabled = false;
  loader = null;
}

export function registeredGptSlotCount() {
  return slots.size;
}

export function defineGptSlotLifecycle({ googletag, elementId, adUnitPath, sizes, responsiveMapping = [], targeting = {}, collapseEmpty = true }) {
  if (!googletag || slots.has(elementId)) return slots.get(elementId) || null;
  if (!/^\/[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/.test(String(adUnitPath || ""))) return null;
  const normalizedSizes = Array.isArray(sizes) ? sizes.filter(size => Array.isArray(size) && size.length === 2 && size.every(n => Number.isInteger(n) && n > 0)) : [];
  if (!normalizedSizes.length) return null;
  const mappingRows = normalizeResponsiveMapping(responsiveMapping, normalizedSizes);
  const managedTargeting = validateManagedTargeting(targeting);
  const slot = googletag.defineSlot(adUnitPath, normalizedSizes, elementId);
  if (!slot) return null;
  slot.addService(googletag.pubads());
  if (googletag.sizeMapping && mappingRows.length) {
    const builder = googletag.sizeMapping();
    mappingRows.forEach(row => builder.addSize(row.viewport, row.sizes));
    slot.defineSizeMapping(builder.build());
  }
  Object.entries(managedTargeting).forEach(([key, value]) => slot.setTargeting(key, value));
  if (collapseEmpty) slot.setCollapseEmptyDiv(true, true);
  slots.set(elementId, slot);
  if (!servicesEnabled) {
    googletag.pubads().disableInitialLoad();
    googletag.enableServices();
    servicesEnabled = true;
  }
  return slot;
}

export function displayGptSlot({ googletag, elementId }) {
  const slot = slots.get(elementId);
  if (!slot || displayed.has(elementId)) return false;
  displayed.add(elementId);
  googletag.display(elementId);
  googletag.pubads().refresh([slot], { changeCorrelator: false });
  return true;
}

export function destroyGptSlot({ googletag, elementId }) {
  const slot = slots.get(elementId);
  if (!slot) return false;
  googletag.destroySlots([slot]);
  slots.delete(elementId);
  displayed.delete(elementId);
  return true;
}

export function loadGpt() {
  if (typeof window === "undefined") return Promise.reject(new Error("Browser required"));
  if (loader) return loader;
  window.googletag = window.googletag || { cmd: [] };
  loader = new Promise((resolve, reject) => {
    if (window.googletag.apiReady) { resolve(window.googletag); return; }
    const existing = [...document.scripts].find(item => item.src === GPT_SCRIPT_URL);
    const script = existing || document.createElement("script");
    script.src = GPT_SCRIPT_URL; script.async = true; script.crossOrigin = "anonymous";
    let finished = false;
    const finish = error => {
      if (finished) return;
      finished = true; clearTimeout(timer);
      script.removeEventListener("error", onError);
      if (error) reject(new Error("GAM loader failed")); else resolve(window.googletag);
    };
    const onError = () => finish(true);
    const timer = setTimeout(() => finish(true), 12000);
    script.addEventListener("error", onError);
    window.googletag.cmd.push(() => finish(false));
    if (!existing) document.head.appendChild(script);
  });
  // Failure remains terminal for this page; a slow script must not be duplicated.
  return loader;
}
